import { axios } from '@ben/api/axios';
import { BaseAIAction } from '@ben/columbia/components/assistant/actions/base';
import {
  FollowUpType,
  InvalidArgumentError,
} from '@ben/columbia/components/assistant/types';

interface PricingFormulaResponse {
  reasoning: string;
  formula: string;
}

export class ExplainPricingFormula extends BaseAIAction {
  constructor() {
    super();
    this.label = 'Explain Formula';
    this.requiresUserPrompt = false;
  }

  override async trigger(): Promise<[FollowUpType, unknown][]> {
    if (!this.args || this.args.length !== 1) {
      throw InvalidArgumentError;
    }

    const response = await axios.post<PricingFormulaResponse>(
      'assistant/',
      {
        toolName: 'explainPricingFormula',
        request: this.args[0],
      },
      {
        baseURL: `${process.env.REACT_APP_API_URL}api/v1.0/`,
      }
    );

    return [
      [FollowUpType.ChatMessage, response.data.reasoning],
      [FollowUpType.SetExecuted, []],
    ];
  }
}

export class CreatePricingFormula extends BaseAIAction {
  configId: number;

  constructor(configId: number) {
    super();
    this.label = 'Create Formula';
    this.requiresUserPrompt = true;
    this.firstMessage = 'Please provide a prompt for the new formula';
    this.configId = configId;
  }

  override async trigger(): Promise<[FollowUpType, unknown][]> {
    if (!this.args || this.args.length !== 1) {
      throw InvalidArgumentError;
    }

    const response = await axios.post<PricingFormulaResponse>(
      'assistant/',
      {
        toolName: 'createPricingFormula',
        request: this.args[0],
        configId: this.configId,
      },
      {
        baseURL: `${process.env.REACT_APP_API_URL}api/v1.0/`,
      }
    );

    const modifyAgent = new ModifyPricingFormula(this.configId);
    modifyAgent.externalSetValue = this.externalSetValue;
    modifyAgent.args = [response.data.formula];

    return [
      [
        FollowUpType.ChatMessage,
        [response.data.reasoning, response.data.formula],
      ],
      [FollowUpType.UpdateInput, response.data.formula],
      [FollowUpType.SwitchOption, modifyAgent],
    ];
  }
}

export class ModifyPricingFormula extends BaseAIAction {
  configId: number;

  constructor(configId: number) {
    super();
    this.label = 'Modify Formula';
    this.requiresUserPrompt = true;
    this.firstMessage = 'What would you like to change?';
    this.configId = configId;
  }

  override async trigger(): Promise<[FollowUpType, unknown][]> {
    if (!this.args || this.args.length !== 2) {
      throw InvalidArgumentError;
    }

    const response = await axios.post<PricingFormulaResponse>(
      'assistant/',
      {
        toolName: 'modifyPricingFormula',
        request: this.args[1],
        formula: this.args[0],
        configId: this.configId,
        previousChatMessages: this.chatMessages?.map((message) => {
          return {
            message:
              message.message +
              (message.background ? '\n' + message.background : ''),
            isUser: message.isUser,
          };
        }),
      },
      {
        baseURL: `${process.env.REACT_APP_API_URL}api/v1.0/`,
      }
    );

    return [
      [
        FollowUpType.ChatMessage,
        [response.data.reasoning, response.data.formula],
      ],
      [FollowUpType.UpdateInput, response.data.formula],
      [FollowUpType.ContinueConversation, [response.data.formula]],
    ];
  }
}
